import {
  NavItemLabelDto,
  PageDto,
} from '@asset-metrix/portal-gateway-client-js';

import { formatPageFilterUrlValuesForDisplay } from '../components/filter/formatter';
import { FilterConfigDto } from '../store/states/filterTypes';

import { DETAILS_PAGE_URL_DISCRIMINATOR_PARAM } from './constants';
import {
  fallbackNavItemLabelsWhenEmpty,
  groupNavItemLabelsByRow,
  RowGroupableNavItemLabel,
} from './detailPageNavItemLabelLines';
import { getDetailsFromSearchParams } from './getDetailsFromSearchParams';

export type ResolveDetailPageNavItemDisplayOptions = {
  detailsDiscriminatorDisplayValue?: string;
};

function buildFilterConfigByKey(
  pageData: PageDto | undefined
): Record<string, FilterConfigDto> {
  const filters = pageData?.pageFilter?.filters ?? [];
  return Object.fromEntries(filters.map((filter) => [filter.key, filter]));
}

function resolveDetailsDiscriminatorDisplayValue(
  searchParams: URLSearchParams,
  pageData: PageDto | undefined,
  options?: ResolveDetailPageNavItemDisplayOptions
): string {
  if (options?.detailsDiscriminatorDisplayValue != null) {
    return options.detailsDiscriminatorDisplayValue;
  }

  const { detailsLabel } = getDetailsFromSearchParams(searchParams, {
    pageData,
  });

  return detailsLabel ?? '';
}

function getDetailsDiscriminatorValues(
  searchParams: URLSearchParams,
  pageData: PageDto | undefined,
  options?: ResolveDetailPageNavItemDisplayOptions
): string[] {
  if (options?.detailsDiscriminatorDisplayValue != null) {
    return [options.detailsDiscriminatorDisplayValue];
  }

  const detailsDiscriminator = searchParams.get(
    DETAILS_PAGE_URL_DISCRIMINATOR_PARAM
  );
  if (detailsDiscriminator == null) {
    return [resolveDetailsDiscriminatorDisplayValue(searchParams, pageData)];
  }

  const values = searchParams.getAll(detailsDiscriminator);
  if (values.length === 0) {
    return [resolveDetailsDiscriminatorDisplayValue(searchParams, pageData)];
  }

  const filterConfigByKey = buildFilterConfigByKey(pageData);
  return values.map((value) =>
    formatPageFilterUrlValuesForDisplay(
      [value],
      filterConfigByKey[detailsDiscriminator]
    )
  );
}

/**
 * Resolver for detail page nav item labels, that can be filled synchronously from the page data.
 * If the config cannot be resolved synchronously, it will return a fallback list of labels.
 */
export function resolveDetailPageNavItemDisplay(
  pageData: PageDto | undefined,
  searchParams: URLSearchParams,
  options?: ResolveDetailPageNavItemDisplayOptions
): NavItemLabelDto[] {
  const detailsDiscriminatorDisplayValue =
    resolveDetailsDiscriminatorDisplayValue(searchParams, pageData, options);

  const config = pageData?.detailPageNavItemConfig;
  if (config == null || config.length === 0) {
    if (detailsDiscriminatorDisplayValue.length > 0) {
      return [
        {
          kind: 'detailsDiscriminatorValue' as const,
          displayValue: detailsDiscriminatorDisplayValue,
        },
      ];
    }
    return [];
  }

  const labels: RowGroupableNavItemLabel[] = [];
  for (const segment of config) {
    // This loop only needs to handle synchronously resolvable labels, because asynchronous segments are handled elsewhere.
    if (segment['kind'] === 'pageTitle') {
      labels.push({
        kind: 'pageTitle' as const,
        displayValues: [pageData?.pageHeader?.title ?? ''],
        groupByRow: false,
      });
    }

    if (segment['kind'] === 'detailsDiscriminatorValue') {
      labels.push({
        kind: 'detailsDiscriminatorValue' as const,
        displayValues: getDetailsDiscriminatorValues(
          searchParams,
          pageData,
          options
        ),
        groupByRow: true,
      });
    }
  }

  if (labels.length === 0) {
    return fallbackNavItemLabelsWhenEmpty(pageData);
  }

  return groupNavItemLabelsByRow(labels);
}
